import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarDays, Filter, X, Download } from 'lucide-react';
import { toast } from 'sonner';

const InvoiceReports = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    client_id: '',
    manager_id: '',
    status: '',
    month: '',
    start_date: '',
    end_date: '',
  });
  
  // Available filter options
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'approved', label: 'Approved' },
    { value: 'invoice_raised', label: 'Invoice Raised' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'sent_for_revision', label: 'Sent for Revision' },
  ];

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [{ value: '', label: 'All Months' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const fetchReports = useCallback(async (filterParams = {}) => {
    setLoading(true);
    try {
      // Clean empty filter values
      const cleanFilters = Object.fromEntries(
        Object.entries(filterParams).filter(([, v]) => v !== '')
      );
      
      const response = await reportsAPI.getFilteredReports(cleanFilters);
      setData(response.data);
      setClients(response.data.filters?.clients || []);
      setManagers(response.data.filters?.managers || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const applyFilters = () => {
    fetchReports(filters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      client_id: '',
      manager_id: '',
      status: '',
      month: '',
      start_date: '',
      end_date: '',
    };
    setFilters(clearedFilters);
    fetchReports(clearedFilters);
  };

  const exportToCSV = () => {
    if (!data?.orders?.length) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['SO Number', 'Client', 'Project', 'Manager', 'Status', 'Subtotal', 'Tax', 'Total', 'Date'];
    const rows = data.orders.map(order => [
      order.order_number,
      order.client_name,
      order.project_name,
      order.manager_name,
      order.status,
      order.subtotal,
      order.tax,
      order.total,
      order.date
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_orders_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (loading && !data) {
    return <div className="text-center py-12">Loading reports...</div>;
  }

  const summary = data?.summary || {};
  const orders = data?.orders || [];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sales Order Reports
          </h1>
          <p className="text-muted-foreground mt-2">Filter and analyze sales orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Client Filter */}
              <div className="space-y-2">
                <Label htmlFor="client-filter">Client</Label>
                <select
                  id="client-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={filters.client_id}
                  onChange={(e) => handleFilterChange('client_id', e.target.value)}
                >
                  <option value="">All Clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              {/* Manager Filter */}
              <div className="space-y-2">
                <Label htmlFor="manager-filter">Manager</Label>
                <select
                  id="manager-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={filters.manager_id}
                  onChange={(e) => handleFilterChange('manager_id', e.target.value)}
                >
                  <option value="">All Managers</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="status-filter">Status</Label>
                <select
                  id="status-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Month Filter */}
              <div className="space-y-2">
                <Label htmlFor="month-filter">Month</Label>
                <select
                  id="month-filter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={filters.month}
                  onChange={(e) => handleFilterChange('month', e.target.value)}
                >
                  {getMonthOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters}>
                Apply Filters
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Sales Orders ({orders.length})</span>
            <span className="text-sm font-normal text-muted-foreground">
              Total: ₹{(summary.total_amount || 0).toLocaleString('en-IN')}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No sales orders found matching the selected filters.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold uppercase text-muted-foreground">SO #</th>
                      <th className="text-left p-3 text-xs font-semibold uppercase text-muted-foreground">Client</th>
                      <th className="text-left p-3 text-xs font-semibold uppercase text-muted-foreground">Project</th>
                      <th className="text-left p-3 text-xs font-semibold uppercase text-muted-foreground">Manager</th>
                      <th className="text-left p-3 text-xs font-semibold uppercase text-muted-foreground">Status</th>
                      <th className="text-right p-3 text-xs font-semibold uppercase text-muted-foreground">Subtotal</th>
                      <th className="text-right p-3 text-xs font-semibold uppercase text-muted-foreground">Tax</th>
                      <th className="text-right p-3 text-xs font-semibold uppercase text-muted-foreground">Total</th>
                      <th className="text-right p-3 text-xs font-semibold uppercase text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-t hover:bg-muted/50">
                        <td className="p-3 font-medium">{order.order_number}</td>
                        <td className="p-3 text-sm">{order.client_name}</td>
                        <td className="p-3 text-sm">{order.project_name}</td>
                        <td className="p-3 text-sm">{order.manager_name}</td>
                        <td className="p-3">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              order.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : order.status === 'approved'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td className="p-3 text-right text-sm">
                          {order.currency} {(order.subtotal || 0).toLocaleString()}
                        </td>
                        <td className="p-3 text-right text-sm">
                          {order.currency} {(order.tax || 0).toLocaleString()}
                        </td>
                        <td className="p-3 text-right font-semibold">
                          {order.currency} {(order.total || 0).toLocaleString()}
                        </td>
                        <td className="p-3 text-right text-sm">{order.date}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/50">
                    <tr>
                      <td colSpan="5" className="p-3 font-semibold text-right">Totals:</td>
                      <td className="p-3 text-right font-semibold">
                        ₹{orders.reduce((sum, o) => sum + (o.subtotal || 0), 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right font-semibold">
                        ₹{orders.reduce((sum, o) => sum + (o.tax || 0), 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-right font-bold">
                        ₹{orders.reduce((sum, o) => sum + (o.total || 0), 0).toLocaleString('en-IN')}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceReports;
